import {
    BellIcon,
    CheckCircleIcon,
    CogIcon,
    EditIcon,
} from 'lucide-react-native';
import { useState } from 'react';
import { XStack, YStack } from 'tamagui';

import {
    Button,
    ButtonSize,
    ButtonVariant,
} from '../../components/buttons/Button';
import { AccountRow } from '../../components/rows/AccountRow';
import { BadgeRow } from '../../components/rows/BadgeRow';
import { CheckboxRow } from '../../components/rows/CheckboxRow';
import { NavigationRow } from '../../components/rows/NavigationRow';
import { RowList } from '../../components/rows/RowList';
import { SwitchRow } from '../../components/rows/SwitchRow';
import { TextRow } from '../../components/rows/TextRow';
import {
    BodyMediumRegular,
    BodySmallRegular,
    H6Regular,
    LabelRegular,
} from '../../components/text/Typography';

export const RowLists = () => {
    const [checkbox1, setCheckbox1] = useState(false);
    const [checkbox2, setCheckbox2] = useState(false);
    const [checkbox3, setCheckbox3] = useState(false);
    const [switch1, setSwitch1] = useState(false);
    const [switch2, setSwitch2] = useState(false);
    const [switch3, setSwitch3] = useState(false);

    return (
        <YStack gap={32}>
            {/* Single Element Examples */}
            <YStack gap={12}>
                <H6Regular>Single Element</H6Regular>
                <YStack gap={8}>
                    <RowList>
                        <TextRow
                            title="Email"
                            secondaryText="user@example.com"
                        />
                    </RowList>
                    <RowList>
                        <NavigationRow title="Settings" href="/" />
                    </RowList>
                    <RowList>
                        <CheckboxRow
                            title="Enable Notifications"
                            onPress={() => {
                                setCheckbox1(!checkbox1);
                            }}
                            checkboxProps={{
                                checked: checkbox1,
                            }}
                        />
                    </RowList>
                    <RowList>
                        <SwitchRow
                            title="Dark Mode"
                            switchProps={{
                                isChecked: switch1,
                                onCheckedChange: setSwitch1,
                            }}
                        />
                    </RowList>
                    <RowList>
                        <AccountRow
                            accountName="Checking Account"
                            isJointAccount={false}
                            isJointMembership={false}
                            balance="$5,234.56"
                        />
                    </RowList>
                </YStack>
            </YStack>

            {/* Multiple Same Type - TextRow */}
            <YStack gap={12}>
                <H6Regular>Multiple Text Rows</H6Regular>
                <RowList>
                    <TextRow title="Email" secondaryText="user@example.com" />
                    <TextRow
                        title="Phone"
                        subtitle="Contact number"
                        secondaryText="(555) 123-4567"
                    />
                    <TextRow
                        title="Account Balance"
                        secondaryText="$1,234.56"
                    />
                </RowList>
            </YStack>

            {/* Multiple Same Type - NavigationRow */}
            <YStack gap={12}>
                <H6Regular>Multiple Navigation Rows</H6Regular>
                <RowList>
                    <NavigationRow title="Settings" href="/" />
                    <NavigationRow
                        title="Profile"
                        subtitle="View your profile"
                        href="/"
                    />
                    <NavigationRow
                        title="Notifications"
                        subtitle="Manage notifications"
                        leftIcon={BellIcon}
                        href="/"
                    />
                </RowList>
            </YStack>

            {/* Multiple Same Type - CheckboxRow */}
            <YStack gap={12}>
                <H6Regular>Multiple Checkbox Rows</H6Regular>
                <RowList>
                    <CheckboxRow
                        title="Email Notifications"
                        checkboxProps={{
                            checked: checkbox1,
                            onPress: () => {
                                setCheckbox1(!checkbox1);
                            },
                        }}
                    />
                    <CheckboxRow
                        title="Push Notifications"
                        subtitle="Receive push notifications"
                        checkboxProps={{
                            checked: checkbox2,
                            onPress: () => {
                                setCheckbox2(!checkbox2);
                            },
                        }}
                    />
                    <CheckboxRow
                        title="SMS Notifications"
                        checkboxProps={{
                            checked: checkbox3,
                            onPress: () => {
                                setCheckbox3(!checkbox3);
                            },
                        }}
                    />
                </RowList>
            </YStack>

            {/* Multiple Same Type - SwitchRow */}
            <YStack gap={12}>
                <H6Regular>Multiple Switch Rows</H6Regular>
                <RowList>
                    <SwitchRow
                        title="Dark Mode"
                        switchProps={{
                            isChecked: switch1,
                            onCheckedChange: setSwitch1,
                        }}
                    />
                    <SwitchRow
                        title="Auto-sync"
                        subtitle="Automatically sync data"
                        switchProps={{
                            isChecked: switch2,
                            onCheckedChange: setSwitch2,
                        }}
                    />
                    <SwitchRow
                        title="Biometric Login"
                        switchProps={{
                            isChecked: switch3,
                            onCheckedChange: setSwitch3,
                        }}
                    />
                </RowList>
            </YStack>

            {/* Multiple Same Type - AccountRow */}
            <YStack gap={12}>
                <H6Regular>Multiple Account Rows</H6Regular>
                <RowList>
                    <AccountRow
                        accountName="Checking Account"
                        isJointAccount={false}
                        isJointMembership={false}
                        balance="$5,234.56"
                        accountIcon="home"
                    />
                    <AccountRow
                        accountName="Savings Account"
                        isJointAccount={true}
                        isJointMembership={true}
                        balance="$12,500.00"
                        accountIcon="piggyBank"
                        accountColor="blue"
                    />
                    <AccountRow
                        accountName="IRA Account"
                        isJointAccount={false}
                        isJointMembership={false}
                        balance="$45,678.90"
                    />
                </RowList>
            </YStack>

            {/* Multiple Same Type - BadgeRow */}
            <YStack gap={12}>
                <H6Regular>Multiple Badge Rows</H6Regular>
                <RowList>
                    <BadgeRow
                        title="Item"
                        badgeProps={{ variant: 'neutral', text: 'Action' }}
                        onPress={() => {}}
                    />
                    <BadgeRow
                        title="Item"
                        subtitle="With subtitle"
                        badgeProps={{ variant: 'positive', text: 'Active' }}
                        onPress={() => {}}
                    />
                    <BadgeRow
                        title="Item"
                        badgeProps={{
                            variant: 'warning',
                            text: 'Warning',
                            icon: CheckCircleIcon,
                        }}
                        onPress={() => {}}
                    />
                </RowList>
            </YStack>

            {/* Mix and Match - Simple */}
            <YStack gap={12}>
                <H6Regular>Mix and Match - Simple</H6Regular>
                <RowList>
                    <TextRow title="Email" secondaryText="user@example.com" />
                    <NavigationRow title="Settings" href="/" />
                    <SwitchRow
                        title="Dark Mode"
                        switchProps={{
                            isChecked: switch1,
                            onCheckedChange: setSwitch1,
                        }}
                    />
                </RowList>
            </YStack>

            {/* Mix and Match - With Account Rows */}
            <YStack gap={12}>
                <H6Regular>Mix and Match - With Account Rows</H6Regular>
                <RowList>
                    <AccountRow
                        accountName="Checking Account"
                        isJointAccount={false}
                        isJointMembership={false}
                        balance="$5,234.56"
                        accountIcon="home"
                        onPress={() => {
                            console.log('Checking account pressed');
                        }}
                    />
                    <AccountRow
                        accountName="IRA Account"
                        isJointAccount={false}
                        isJointMembership={false}
                        balance="$45,678.90"
                        accountIcon="briefCase"
                        accountColor="purple"
                        onPress={() => {
                            console.log('IRA account pressed');
                        }}
                    />
                    <TextRow title="Total Balance" secondaryText="$50,913.46" />
                </RowList>
            </YStack>

            {/* Mix and Match - Comprehensive */}
            <YStack gap={12}>
                <H6Regular>Mix and Match - Comprehensive</H6Regular>
                <RowList>
                    <TextRow
                        title="Account Information"
                        secondaryText="Verified"
                    />
                    <AccountRow
                        accountName="Checking Account"
                        isJointAccount={false}
                        isJointMembership={false}
                        balance="$5,234.56"
                        accountIcon="home"
                    />
                    <AccountRow
                        accountName="Savings Account"
                        isJointAccount={false}
                        isJointMembership={false}
                        balance="$12,500.00"
                        accountIcon="piggyBank"
                        accountColor="blue"
                    />
                    <NavigationRow
                        title="View All Accounts"
                        href="/"
                        leftIcon={CogIcon}
                    />
                    <SwitchRow
                        title="Auto-transfer"
                        subtitle="Automatically transfer funds"
                        switchProps={{
                            isChecked: switch1,
                            onCheckedChange: setSwitch1,
                        }}
                    />
                    <CheckboxRow
                        title="Enable notifications"
                        checkboxProps={{
                            checked: checkbox1,
                            onPress: () => {
                                setCheckbox1(!checkbox1);
                            },
                        }}
                    />
                </RowList>
            </YStack>

            {/* Mix and Match - Settings Style */}
            <YStack gap={12}>
                <H6Regular>Mix and Match - Settings Style</H6Regular>
                <RowList>
                    <TextRow
                        title="Profile"
                        subtitle="Personal information"
                        secondaryText="Complete"
                    />
                    <NavigationRow
                        title="Edit Profile"
                        href="/"
                        leftIcon={EditIcon}
                    />
                    <SwitchRow
                        title="Two-Factor Authentication"
                        subtitle="Add an extra layer of security"
                        switchProps={{
                            isChecked: switch2,
                            onCheckedChange: setSwitch2,
                        }}
                    />
                    <CheckboxRow
                        title="Email notifications"
                        checkboxProps={{
                            checked: checkbox2,
                            onPress: () => {
                                setCheckbox2(!checkbox2);
                            },
                        }}
                    />
                </RowList>
            </YStack>

            {/* Arbitrary Content - Typography and Components */}
            <YStack gap={12}>
                <H6Regular>Arbitrary Content</H6Regular>
                <YStack gap={8}>
                    <RowList>
                        <YStack padding={16} gap={8}>
                            <LabelRegular>Account Summary</LabelRegular>
                            <BodyMediumRegular>
                                This is an example of arbitrary content within a
                                RowList. You can include any components you
                                need.
                            </BodyMediumRegular>
                            <BodySmallRegular color="$gray700">
                                RowLists accept any ReactNode as children, not
                                just row components.
                            </BodySmallRegular>
                        </YStack>
                    </RowList>
                    <RowList>
                        <YStack padding={16} gap={12}>
                            <XStack
                                alignItems="center"
                                justifyContent="space-between"
                            >
                                <YStack gap={4}>
                                    <LabelRegular>
                                        Payment Information
                                    </LabelRegular>
                                    <BodySmallRegular color="$gray700">
                                        Manage your payment settings and
                                        preferences
                                    </BodySmallRegular>
                                </YStack>
                            </XStack>
                            <XStack gap={8} justifyContent="flex-end">
                                <Button
                                    variant={ButtonVariant.SECONDARY}
                                    size={ButtonSize.SMALL}
                                    text="Cancel"
                                    onPress={() => {}}
                                />
                                <Button
                                    variant={ButtonVariant.PRIMARY}
                                    size={ButtonSize.SMALL}
                                    text="Save"
                                    onPress={() => {}}
                                />
                            </XStack>
                        </YStack>
                    </RowList>
                </YStack>
            </YStack>
        </YStack>
    );
};
